// ── Dock assembly generator ──────────────────────────────────────────────────────────────────
// Turns what a user drew (features: walkways, fingers, piles) into catalog components, the
// connections between them, piles with their guides, a bill of materials and rule checks.
//
// A pure function of (features, catalog release, rules): nothing is cached or stored, so
// rebuilding is always safe and a catalog change reaches every assembly the next time it is
// built. Feature ids are stable, and component ids derive from them (`<feature>:m<index>`), so a
// rebuild of unchanged features gives the same ids.
//
// Works in a local frame in feet (x east, y north) around the assembly's origin; localFrame()
// converts to and from lon/lat (equirectangular -- under 1 cm of error across a marina).
//
// Features:
//   walkway  { points: [[x,y], ...] }  params { width, seams?: [ft along the run], part?: {...},
//            attach?: { walkway: <feature id>, station: ft along that walkway } -- a connection
//            point: this walkway's start (points[0], already drawn at the host's edge) is the
//            branch of a T or cross junction into the named host walkway, instead of a
//            free-standing end }
//   finger   { }  params { walkway: <feature id>, station: ft along the walkway, side: 1 (left
//            of the drawn direction) | -1 (right), angle?: deg off perpendicular, length, width,
//            tip ("round" | "square" | "T-head"), frame?: {...} }
//   pile     { point: [x,y] }  params { section, size, topElev, tipElev, guide?: {type, clearance} }
//   ramp     { point: [x,y] (the shore-side landing) }  params { walkway: <feature id>, station:
//            ft along the walkway, side: 1 | -1, width, shoreRise, handrailHeight, surface } -- a
//            gangway from the walkway's edge to a fixed point on shore
//   accessory { point: [x,y] } params { type: "power-pedestal" | "dock-box" | "cleat" | "gate" |
//            "mooring-whip" | "fire-station" | "life-ring-station" | "fence-panel" |
//            "security-light" | "trash-receptacle", rotation?: deg off the host float's heading
//            (ignored by "gate", which always spans the host's full width), ...part params } --
//            stands on the float under it

import { catalogAt, resolveParams } from "./catalog.js";

const FT_PER_M = 3.280839895;

export function localFrame(lon0, lat0) {
  const rad = (lat0 * Math.PI) / 180;
  // WGS84 metres per degree at this latitude
  const mLat = 111132.92 - 559.82 * Math.cos(2 * rad) + 1.175 * Math.cos(4 * rad);
  const mLon = 111412.84 * Math.cos(rad) - 93.5 * Math.cos(3 * rad);
  const fx = mLon * FT_PER_M, fy = mLat * FT_PER_M;
  return {
    origin: [lon0, lat0],
    toLocal: ([lon, lat]) => [(lon - lon0) * fx, (lat - lat0) * fy],
    toLonLat: ([x, y]) => [lon0 + x / fx, lat0 + y / fy],
  };
}

// ── geometry helpers ──
const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const mul = (a, k) => [a[0] * k, a[1] * k];
const len = (a) => Math.hypot(a[0], a[1]);
const unit = (a) => { const l = len(a) || 1; return [a[0] / l, a[1] / l]; };
const left = (a) => [-a[1], a[0]];
const rot = (a, ang) => [a[0] * Math.cos(ang) - a[1] * Math.sin(ang), a[0] * Math.sin(ang) + a[1] * Math.cos(ang)];
// Rectangle centred on c, long axis along unit u, half-length hl, half-width hw.
const rect = (c, u, hl, hw) => { const n = left(u); return [add(add(c, mul(u, -hl)), mul(n, -hw)), add(add(c, mul(u, hl)), mul(n, -hw)), add(add(c, mul(u, hl)), mul(n, hw)), add(add(c, mul(u, -hl)), mul(n, hw))]; };
function pointInPoly(p, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
function distToPoly(p, poly) {
  let best = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[j], d = sub(poly[i], a), L2 = d[0] * d[0] + d[1] * d[1];
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * d[0] + (p[1] - a[1]) * d[1]) / L2)) : 0;
    best = Math.min(best, len(sub(p, add(a, mul(d, t)))));
  }
  return best;
}

// Module lengths for a straight stretch: full-length modules, then the remainder; a remainder
// shorter than the minimum is merged with the last full module and the pair split evenly.
export function splitModules(total, std, min) {
  if (total <= 1e-6) return [];
  if (total <= std + 1e-6) return [total];
  const n = Math.floor(total / std + 1e-9), r = total - n * std;
  if (r < 1e-6) return Array(n).fill(std);
  if (r >= min) return [...Array(n).fill(std), r];
  return [...Array(n - 1).fill(std), (std + r) / 2, (std + r) / 2];
}

// A walkway centreline as stretches between vertices: corner modules sit on interior vertices,
// so each stretch is shortened by half the width at an interior end.
function walkwayStretches(points, width) {
  const out = [];
  let along = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1], L = len(sub(b, a)), u = unit(sub(b, a));
    const cutA = i > 0 ? width / 2 : 0, cutB = i < points.length - 2 ? width / 2 : 0;
    out.push({ a, b, u, L, from: along + cutA, to: along + L - cutB, start: along });
    along += L;
  }
  return { stretches: out, total: along };
}

// Point, direction and stretch index at a distance along a walkway's centreline.
function along(stretches, s) {
  for (const st of stretches) {
    if (s <= st.start + st.L + 1e-6) { const t = Math.max(0, s - st.start); return { p: add(st.a, mul(st.u, t)), u: st.u }; }
  }
  const st = stretches[stretches.length - 1];
  return { p: st.b, u: st.u };
}

export function buildAssembly({ features = [], system = "waler-concrete", catalogVersion = null, rules: ruleOverrides = {} } = {}) {
  const release = catalogAt(catalogVersion);
  const sys = release.systems[system];
  if (!sys) throw new Error(`no dock system "${system}" in catalog ${release.version}`);
  const rules = { ...sys.rules, ...ruleOverrides };
  const P = sys.parts;
  const components = [], connections = [], piles = [], warnings = [];
  const comp = (c) => { components.push({ version: release.version, ...c }); return c; };
  const walkways = new Map();

  // walkways: corner modules at bends, float modules along each stretch, seam joints between.
  // A walkway with params.attach is a branch into another walkway's side: a connection point
  // (walkway-junction) bridges from the host's edge to the branch's first module, the same way a
  // finger-frame bridges a finger to its walkway -- so T-docks, cross-docks and fishbone layouts
  // are drawn by attaching one walkway feature to another, not by one feature's own bends.
  function buildWalkway(f) {
    const pts = f.geometry?.points || [];
    if (pts.length < 2) { warnings.push({ feature: f.id, message: "walkway needs at least two points" }); return; }
    const mod = resolveParams(P["walkway-module"], { ...(f.params?.part || {}), width: f.params?.width });
    const { stretches, total } = walkwayStretches(pts, mod.width);
    const seams = Array.isArray(f.params?.seams) ? f.params.seams.map(Number).filter((s) => s > 0 && s < total).sort((a, b) => a - b) : null;
    const mods = [];
    let k = 0;
    stretches.forEach((st, si) => {
      let lens;
      if (seams) { // user-placed seams: cut this stretch at the seams that fall inside it
        const cuts = [st.from, ...seams.filter((s) => s > st.from + 1e-6 && s < st.to - 1e-6), st.to];
        lens = cuts.slice(1).map((c, i) => c - cuts[i]);
      } else lens = splitModules(st.to - st.from, mod.length, rules.minModuleLength);
      let s = st.from;
      for (const L of lens) {
        const c = add(st.a, mul(st.u, s - st.start + L / 2));
        const params = { ...mod, length: Math.round(L * 100) / 100 };
        if (L > P["walkway-module"].params.length.max + 1e-6) warnings.push({ feature: f.id, message: `module ${k} is ${L.toFixed(1)} ft, longer than the ${P["walkway-module"].params.length.max} ft maximum` });
        mods.push(comp({ id: `${f.id}:m${k++}`, feature: f.id, part: "walkway-module", params, center: c, heading: Math.atan2(st.u[1], st.u[0]), from: s, to: s + L, footprint: rect(c, st.u, L / 2, mod.width / 2) }));
        s += L;
      }
      if (si < stretches.length - 1) { // corner module on the vertex
        const v = st.b, u2 = stretches[si + 1].u, bis = unit(add(st.u, u2));
        mods.push(comp({ id: `${f.id}:c${si}`, feature: f.id, part: "corner-module", params: resolveParams(P["corner-module"], { width: mod.width, freeboard: mod.freeboard, depth: mod.depth }), center: v, heading: Math.atan2(bis[1], bis[0]), from: st.to, to: stretches[si + 1].from, footprint: rect(v, st.u, mod.width / 2, mod.width / 2) }));
      }
    });
    for (let i = 0; i < mods.length - 1; i++) {
      const a = mods[i], b = mods[i + 1];
      connections.push({ id: `${a.id}~${b.id}`, kind: b.part === "corner-module" || a.part === "corner-module" ? "corner-joint" : "seam", a: a.id, b: b.id, joint: "semi-rigid", at: along(stretches, a.to).p });
    }
    const att = f.params?.attach;
    if (att?.walkway) {
      const host = walkways.get(att.walkway);
      if (!host) warnings.push({ feature: f.id, message: "junction host walkway wasn't found; built unattached" });
      else {
        const station = Number(att.station);
        if (!(station >= 0 && station <= host.total)) warnings.push({ feature: f.id, message: `junction station ${att.station} is off the host walkway (0 to ${host.total.toFixed(1)} ft)` });
        else {
          const { u: hostU } = along(host.stretches, station);
          const jp = resolveParams(P["walkway-junction"], att);
          const at = mods[0] ? mods[0].center : pts[0]; // the branch's own first point is the connection point
          const junctionWidth = Math.max(host.width, mod.width);
          const junction = comp({ id: `${f.id}:j`, feature: f.id, part: "walkway-junction", params: jp, center: pts[0], heading: Math.atan2(hostU[1], hostU[0]), width: junctionWidth, footprint: rect(pts[0], hostU, jp.armLength, junctionWidth / 2) });
          const hostMod = host.mods.find((m) => station >= m.from - 1e-6 && station <= m.to + 1e-6) || host.mods[0];
          connections.push({ id: `${junction.id}~${hostMod.id}`, kind: "junction-to-host", a: junction.id, b: hostMod.id, joint: "rigid", at: pts[0] });
          if (mods[0]) connections.push({ id: `${junction.id}~${mods[0].id}`, kind: "junction-to-branch", a: junction.id, b: mods[0].id, joint: "rigid", at });
        }
      }
    }
    walkways.set(f.id, { feature: f, stretches, total, mods, width: mod.width });
  }
  {
    const walkwayFeatures = features.filter((x) => x.kind === "walkway");
    let remaining = walkwayFeatures, stalled = false;
    while (remaining.length) {
      const next = [];
      for (const f of remaining) {
        const hostId = f.params?.attach?.walkway;
        if (hostId && !stalled && !walkways.has(hostId) && walkwayFeatures.some((w) => w.id === hostId)) { next.push(f); continue; }
        buildWalkway(f);
      }
      stalled = next.length === remaining.length; // no progress: a cycle, or a self-reference -- force-build the rest
      remaining = next;
    }
  }

  // fingers on their walkway, each with a knee-braced frame at the root
  for (const f of features.filter((x) => x.kind === "finger")) {
    const w = walkways.get(f.params?.walkway);
    if (!w) { warnings.push({ feature: f.id, message: "finger is not attached to a walkway" }); continue; }
    const station = Number(f.params?.station);
    if (!(station >= 0 && station <= w.total)) { warnings.push({ feature: f.id, message: `station ${f.params?.station} is off the walkway (0 to ${w.total.toFixed(1)} ft)` }); continue; }
    const side = Number(f.params?.side) < 0 ? -1 : 1;
    const fp = resolveParams(P.finger, f.params);
    const { p, u } = along(w.stretches, station);
    const out = mul(left(u), side), axis = rot(out, ((Number(f.params?.angle) || 0) * Math.PI) / 180);
    const root = add(p, mul(out, w.width / 2)), c = add(root, mul(axis, fp.length / 2));
    const finger = comp({ id: `${f.id}:f`, feature: f.id, part: "finger", params: fp, center: c, heading: Math.atan2(axis[1], axis[0]), root, tip: add(root, mul(axis, fp.length)), footprint: fingerOutline(root, axis, fp) });
    const fr = resolveParams(P["finger-frame"], f.params?.frame);
    const along2 = left(axis), hw = fp.width / 2;
    const knees = [1, -1].map((s) => [add(root, mul(along2, s * hw)), add(root, mul(along2, s * (hw + fr.kneeAlongWalk))), add(root, add(mul(along2, s * hw), mul(axis, fr.kneeAlongFinger)))]);
    const frame = comp({ id: `${f.id}:fr`, feature: f.id, part: "finger-frame", params: fr, center: root, heading: finger.heading, footprint: knees[0], footprints: knees });
    const host = w.mods.find((m) => station >= m.from - 1e-6 && station <= m.to + 1e-6) || w.mods[0];
    connections.push({ id: `${host.id}~${frame.id}`, kind: "frame-to-walkway", a: host.id, b: frame.id, joint: "rigid", at: root });
    connections.push({ id: `${frame.id}~${finger.id}`, kind: "frame-to-finger", a: frame.id, b: finger.id, joint: "rigid", at: root });
  }

  // gangways: a ramp from a walkway's edge out to a fixed point on shore
  for (const f of features.filter((x) => x.kind === "ramp")) {
    const w = walkways.get(f.params?.walkway);
    if (!w) { warnings.push({ feature: f.id, message: "gangway is not attached to a walkway" }); continue; }
    const station = Number(f.params?.station);
    if (!(station >= 0 && station <= w.total)) { warnings.push({ feature: f.id, message: `station ${f.params?.station} is off the walkway (0 to ${w.total.toFixed(1)} ft)` }); continue; }
    const shore = f.geometry?.point;
    if (!shore) { warnings.push({ feature: f.id, message: "gangway has no shore landing point" }); continue; }
    const side = Number(f.params?.side) < 0 ? -1 : 1;
    const gp = resolveParams(P.gangway, f.params);
    const { p, u } = along(w.stretches, station);
    const root = add(p, mul(mul(left(u), side), w.width / 2));
    const run = sub(shore, root), runLen = len(run) || 1, axis = unit(run);
    gp.length = Math.round(runLen * 100) / 100; // drawn, not a catalog default -- read back by the BOM and the 3D model
    const slope = runLen > 1e-6 ? Math.abs(gp.shoreRise) / runLen : 0;
    const ramp = comp({ id: `${f.id}:r`, feature: f.id, part: "gangway", params: gp, center: add(root, mul(axis, runLen / 2)), heading: Math.atan2(axis[1], axis[0]), root, shore, slope, footprint: rect(add(root, mul(axis, runLen / 2)), axis, runLen / 2, gp.width / 2) });
    const host = w.mods.find((m) => station >= m.from - 1e-6 && station <= m.to + 1e-6) || w.mods[0];
    connections.push({ id: `${host.id}~${ramp.id}`, kind: "ramp-to-walkway", a: host.id, b: ramp.id, joint: "rigid", at: root });
  }

  // piles, each guided by the float it passes through (internal well) or sits beside (external hoop)
  const floats = components.filter((c) => P[c.part]?.kind === "float");
  for (const f of features.filter((x) => x.kind === "pile")) {
    const pt = f.geometry?.point;
    if (!pt) { warnings.push({ feature: f.id, message: "pile has no position" }); continue; }
    const pp = resolveParams(P.pile, f.params);
    let host = null, best = Infinity, inside = false;
    for (const c of floats) {
      if (pointInPoly(pt, c.footprint)) { host = c; inside = true; break; }
      const d = distToPoly(pt, c.footprint);
      if (d < best) { best = d; host = c; }
    }
    const reach = rules.pileGuideReach + pp.size / 24;
    let guide = null;
    if (host && (inside || best <= reach)) {
      const gp = resolveParams(P["pile-guide"], { type: inside ? "internal" : "external", ...(f.params?.guide || {}) });
      guide = comp({ id: `${f.id}:g`, feature: f.id, part: "pile-guide", params: gp, center: pt, heading: host.heading, footprint: rect(pt, [Math.cos(host.heading), Math.sin(host.heading)], pp.size / 24 + 0.4, pp.size / 24 + 0.4) });
      connections.push({ id: `${f.id}:p~${guide.id}`, kind: "pile-to-guide", a: `${f.id}:p`, b: guide.id, joint: "slide-vertical", at: pt });
      connections.push({ id: `${guide.id}~${host.id}`, kind: "guide-to-float", a: guide.id, b: host.id, joint: "rigid", at: pt });
    } else warnings.push({ feature: f.id, message: "pile is not next to any float, so nothing is held by it" });
    piles.push(comp({ id: `${f.id}:p`, feature: f.id, part: "pile", params: pp, center: pt, heading: host?.heading || 0, footprint: rect(pt, [1, 0], pp.size / 24, pp.size / 24), holds: guide ? host.id : null }));
  }

  // fittings (power posts, dock boxes, cleats, gates and the rest) on the deck of the float they
  // stand on. A gate is a fitting too, except it always spans the host float's full width instead
  // of sitting at a fixed size and rotation -- it is the dock's width, turned into a door.
  for (const f of features.filter((x) => x.kind === "accessory")) {
    const pt = f.geometry?.point, type = f.params?.type;
    const kind = P[type]?.kind;
    if (!pt || (kind !== "fitting" && kind !== "gate")) { warnings.push({ feature: f.id, message: `unknown fitting "${type}"` }); continue; }
    let host = null, best = Infinity;
    for (const c of floats) {
      if (pointInPoly(pt, c.footprint)) { host = c; best = 0; break; }
      const d = distToPoly(pt, c.footprint); if (d < best) { best = d; host = c; }
    }
    if (!host || best > 1) { warnings.push({ feature: f.id, message: `${P[type].label.toLowerCase()} is not on a dock` }); continue; }
    const fp = resolveParams(P[type], f.params);
    const isGate = kind === "gate";
    const heading = isGate ? host.heading + Math.PI / 2 : host.heading + ((Number(f.params?.rotation) || 0) * Math.PI) / 180;
    const u = [Math.cos(heading), Math.sin(heading)];
    // half-length, half-width of the footprint: from the part's own length/width where it has them,
    // else a fixed plan size (small fittings with no footprint-shaping dimension of their own).
    const FITTING_FOOTPRINT = {
      "dock-box": (q) => [q.length / 2, q.width / 2],
      "power-pedestal": (q) => [q.depth / 2, q.width / 2],
      "fence-panel": (q) => [q.length / 2, 0.1],
      "mooring-whip": (q) => [q.length / 2, 0.3],
      "fire-station": () => [1, 0.5],
      "life-ring-station": () => [0.5, 0.5],
      "security-light": () => [0.4, 0.4],
      "trash-receptacle": () => [1, 1],
    };
    const [hl, hw] = isGate ? [(Number(host.params.width) || 6) / 2, 0.15] : (FITTING_FOOTPRINT[type] || ((q) => [q.length / 2, 0.15]))(fp);
    comp({ id: `${f.id}:a`, feature: f.id, part: type, params: fp, center: pt, heading, host: host.id, deckZ: host.params.freeboard, footprint: rect(pt, u, hl, hw), ...(isGate ? { span: hl } : {}) });
    connections.push({ id: `${f.id}:a~${host.id}`, kind: "fitting-to-float", a: `${f.id}:a`, b: host.id, joint: "rigid", at: pt });
  }

  return { catalogVersion: release.version, system, components, connections, piles, warnings, bom: billOfMaterials(components, P), checks: ruleChecks(walkways, components, rules) };
}

// Finger outline: a rectangle, with a half-round tip when tip = round, or a wider mooring
// platform (a small crossbar) at the tip when tip = T-head.
function fingerOutline(root, axis, fp) {
  const n = left(axis), hw = fp.width / 2;
  const pts = [add(root, mul(n, -hw))];
  if (fp.tip === "round") {
    const c = add(root, mul(axis, fp.length - hw));
    pts.push(add(c, mul(n, -hw)));
    for (let i = 1; i < 8; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 8; pts.push(add(c, add(mul(axis, hw * Math.cos(a)), mul(n, hw * Math.sin(a))))); }
    pts.push(add(c, mul(n, hw)));
  } else if (fp.tip === "T-head") {
    const headHW = hw * 2.2, headLen = Math.min(8, Math.max(4, fp.length * 0.2)), shaftEnd = fp.length - headLen;
    pts.push(add(add(root, mul(axis, shaftEnd)), mul(n, -hw)));
    pts.push(add(add(root, mul(axis, shaftEnd)), mul(n, -headHW)));
    pts.push(add(add(root, mul(axis, fp.length)), mul(n, -headHW)));
    pts.push(add(add(root, mul(axis, fp.length)), mul(n, headHW)));
    pts.push(add(add(root, mul(axis, shaftEnd)), mul(n, headHW)));
    pts.push(add(add(root, mul(axis, shaftEnd)), mul(n, hw)));
  } else {
    pts.push(add(add(root, mul(axis, fp.length)), mul(n, -hw)), add(add(root, mul(axis, fp.length)), mul(n, hw)));
  }
  pts.push(add(root, mul(n, hw)));
  return pts;
}

// Bill of materials: every component's catalog lines, summed by item + spec.
export function billOfMaterials(components, parts) {
  const rows = new Map();
  for (const c of components) {
    const part = parts[c.part];
    if (!part?.bom) continue;
    for (const line of part.bom(c.params)) {
      const key = `${line.item}|${line.spec}|${line.unit}`;
      const r = rows.get(key) || { item: line.item, spec: line.spec, unit: line.unit, qty: 0, parts: 0 };
      r.qty += line.qty; r.parts += 1;
      rows.set(key, r);
    }
  }
  return [...rows.values()].map((r) => ({ ...r, qty: Math.round(r.qty * 100) / 100 })).sort((a, b) => a.item.localeCompare(b.item) || a.spec.localeCompare(b.spec));
}

// Layout checks against the published guidelines. Advisory: they flag, never block.
// Slip-dependent checks (finger length and width against slip length, fairways) come with slip links.
export function ruleChecks(walkways, components, rules = {}) {
  const out = [];
  for (const [id, w] of walkways) {
    const min = w.total >= 300 ? 8 : 6;
    out.push({ feature: id, check: "Main walk width", ok: w.width >= min, value: `${w.width} ft`, rule: `at least ${min} ft${w.total >= 300 ? " for walks of 300 ft or more" : ""}`, source: w.total >= 300 ? "CA DBW 2005" : "UFC 4-152-07" });
  }
  for (const c of components.filter((x) => x.part === "finger")) {
    out.push({ feature: c.feature, check: "Finger width", ok: c.params.width >= 3, value: `${c.params.width} ft`, rule: "at least 3 ft", source: "UFC 4-152-07" });
  }
  const slopeMax = rules.gangwaySlope?.generalMax ?? 1 / 4;
  for (const c of components.filter((x) => x.part === "gangway")) {
    out.push({ feature: c.feature, check: "Gangway slope", ok: c.slope <= slopeMax, value: `1:${c.slope > 0 ? (1 / c.slope).toFixed(1) : "∞"}`, rule: `no steeper than 1:${Math.round(1 / slopeMax)} (1:12 to also meet the ADA accessible-route slope)`, source: "ADA 2010 / ABYC" });
  }
  // Freeboard checks wait for buoyancy-based freeboard: a scanned deck height is tide-dependent.
  return out;
}

// Where a point sits relative to a walkway centreline: distance along it (station), signed offset
// (+ left of the drawn direction), and the distance from the centreline.
export function projectOnWalkway(points, p) {
  let best = null, along = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], d = sub(points[i + 1], a), L = len(d), u = unit(d);
    const t = Math.max(0, Math.min(L, (p[0] - a[0]) * u[0] + (p[1] - a[1]) * u[1]));
    const q = add(a, mul(u, t)), off = (p[0] - q[0]) * -u[1] + (p[1] - q[1]) * u[0], dist = len(sub(p, q));
    if (!best || dist < best.dist) best = { station: along + t, offset: off, dist, seg: i, segStart: along };
    along += L;
  }
  return best ? { ...best, total: along } : null;
}

// Fingers from the slips along one side of a walkway. The slip inventory draws finger slips
// edge to edge on the finger centrelines (LLM: neighbours within 0.5 ft), so each boundary
// between neighbouring slips is a finger, and so are the outer edges of the run's end slips.
// Each finger takes the deeper of its two slips as its length, measured from the walkway edge.
export function fingersFromSlips(points, width, side, slipRings, { maxGap = 6, edgeTol = 4 } = {}) {
  const half = width / 2, slips = [];
  for (const ring of slipRings) {
    if (ring.length < 3) continue;
    // project the whole slip onto the one walkway leg nearest its centre (a slip by a bend would
    // otherwise straddle two legs and look wider than it is)
    const c = ring.reduce((m, p) => [m[0] + p[0] / ring.length, m[1] + p[1] / ring.length], [0, 0]);
    const home = projectOnWalkway(points, c);
    const a = points[home.seg], u = unit(sub(points[home.seg + 1], a));
    const pr = ring.map((p) => ({ station: home.segStart + (p[0] - a[0]) * u[0] + (p[1] - a[1]) * u[1], offset: (p[0] - a[0]) * -u[1] + (p[1] - a[1]) * u[0], total: home.total }));
    const offs = pr.map((q) => side * q.offset);
    const inner = Math.min(...offs), outer = Math.max(...offs);
    if (inner < half - 1.5 || inner > half + edgeTol) continue; // not against this side's edge
    const s0 = Math.min(...pr.map((q) => q.station)), s1 = Math.max(...pr.map((q) => q.station));
    if (s0 <= 0.01 || s1 >= pr[0].total - 0.01) continue; // runs off the walkway's ends
    if (outer - inner < 1.2 * (s1 - s0)) continue; // lies along the walkway (a side tie, or a slip off another leg)
    slips.push({ s0, s1, depth: outer - half });
  }
  slips.sort((a, b) => a.s0 - b.s0);
  const out = [];
  slips.forEach((s, i) => {
    const prev = slips[i - 1];
    if (!prev || s.s0 - prev.s1 > maxGap) out.push({ station: s.s0, length: s.depth }); // a run starts
    const next = slips[i + 1];
    if (next && next.s0 - s.s1 <= maxGap) out.push({ station: (s.s1 + next.s0) / 2, length: Math.max(s.depth, next.depth) });
    else out.push({ station: s.s1, length: s.depth }); // a run ends
  });
  return out.map((f) => ({ station: Math.round(f.station * 100) / 100, length: Math.round(f.length * 2) / 2 }));
}

// Suggested fittings (not saved), by the layout seen at LLM and the reference marinas: a power post
// on the walkway beside each finger root (just past the knee brace), and two cleats along each side
// of every finger. Skips any spot that already has a fitting of that type within 1.5 ft.
export function suggestFittings(built, { posts = true, cleats = true } = {}) {
  const have = built.components.filter((c) => c.part === "power-pedestal" || c.part === "cleat" || c.part === "dock-box");
  const free = (type, at) => !have.some((c) => c.part === type && len(sub(c.center, at)) < 1.5);
  const out = [];
  for (const c of built.components.filter((x) => x.part === "finger")) {
    const axis = [Math.cos(c.heading), Math.sin(c.heading)], n = left(axis), hw = c.params.width / 2, L = c.params.length;
    const kneeEnd = built.components.find((x) => x.id === `${c.feature}:fr`)?.params.kneeAlongWalk ?? 4;
    if (posts) { const at = add(add(c.root, mul(axis, -1.1)), mul(n, hw + kneeEnd + 1)); if (free("power-pedestal", at)) out.push({ at, type: "power-pedestal", reason: "finger root", heading: c.heading }); }
    if (cleats) for (const s of [1, -1]) for (const frac of [0.3, 0.85]) {
      const at = add(add(c.root, mul(axis, L * frac)), mul(n, s * (hw - 0.3)));
      if (free("cleat", at)) out.push({ at, type: "cleat", reason: "finger", heading: c.heading });
    }
  }
  return out;
}

// Suggested piles (not saved): one off each finger tip and along each walkway at the rule spacing,
// alternating sides. The drawing tool shows these for the user to accept, move or drop.
export function suggestPiles(built, { walkSpacing = 40, fingerTips = true, size = 16 } = {}) {
  const out = [], off = size / 24 + 0.25;
  if (fingerTips) for (const c of built.components.filter((x) => x.part === "finger")) {
    const axis = [Math.cos(c.heading), Math.sin(c.heading)];
    out.push({ at: add(c.tip, mul(axis, off)), reason: "finger tip", component: c.id });
  }
  const byFeature = new Map();
  for (const c of built.components.filter((x) => x.part === "walkway-module")) {
    if (!byFeature.has(c.feature)) byFeature.set(c.feature, []);
    byFeature.get(c.feature).push(c);
  }
  for (const mods of byFeature.values()) {
    let next = walkSpacing / 2, side = 1;
    for (const m of mods) {
      const u = [Math.cos(m.heading), Math.sin(m.heading)];
      while (next >= m.from && next <= m.to) {
        const p = add(add(m.center, mul(u, next - (m.from + m.to) / 2)), mul(left(u), side * (m.params.width / 2 + off)));
        out.push({ at: p, reason: "walkway spacing", component: m.id });
        next += walkSpacing; side = -side;
      }
    }
  }
  return out;
}
